import { getPsychologistId, getValidToken, isImpersonated } from '../hooks/api/auth/tokenManager';

/**
 * "Entrar como una psicóloga" (psima) abre la app con un código de acceso de un solo uso. Ese código canjea una sesión NORMAL de
 * esa psicóloga — el backend no la marca como impersonada (`impersonator_id`) — así que en el celular de quien entra es
 * indistinguible de la psicóloga real. Pero el celular NO es de ella: sus notificaciones están registradas a nombre de
 * quien activó en este dispositivo.
 *
 * Si en esa sesión se activaran/registraran las push (y el bloqueo por notificaciones EXIGE activarlas para entrar), el token
 * del celular se reasignaría a la cuenta de la otra persona: le llegarían a ella las notificaciones del celular y a su dueña
 * se le apagarían. Por eso una sesión así se trata como una impersonada a los efectos de las push: no se bloquea, no se
 * registra, no se apaga nada.
 *
 * La marca guarda una huella del token de ESA sesión (los últimos caracteres de la firma del JWT): cualquier ingreso nuevo
 * (Google, otro código) genera otro token y la marca deja de valer sola. La clave NO empieza con `psi-` a propósito: ese
 * prefijo lo borra `clearSession()`.
 */
const KEY = 'pwa-push-foreign-session';

function fingerprint(): string | null {
  const token = getValidToken();
  return token ? token.slice(-24) : null;
}

/**
 * Llamar justo DESPUÉS de guardar la sesión canjeada con `ownerBefore` = de quién era este celular ANTES de canjear
 * (`currentDeviceOwner()` de `pushDevice`, leído antes de pisar la sesión).
 */
export function markForeignSessionIfNeeded(ownerBefore: string | null): void {
  try {
    const now = getPsychologistId();
    if (ownerBefore && now && ownerBefore !== now) {
      const fp = fingerprint();
      if (fp) localStorage.setItem(KEY, fp);
    } else {
      localStorage.removeItem(KEY);
    }
  } catch {
    /* noop: sin storage no se puede marcar (queda el comportamiento anterior) */
  }
}

export function isForeignPushSession(): boolean {
  try {
    const saved = localStorage.getItem(KEY);
    return !!saved && saved === fingerprint();
  } catch {
    return false;
  }
}

/**
 * ¿Esta sesión NO puede tocar las push del celular? Impersonada (JWT con `impersonator_id`) o entrada con el código de
 * acceso de otra persona. En esos casos no se bloquea la app, no se registra ni se apaga ningún dispositivo.
 */
export function isPushSessionRestricted(): boolean {
  return isImpersonated() || isForeignPushSession();
}
